import React, { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CreditCard,
  Loader2,
  RefreshCw,
  CheckCircle2,
  PackageCheck,
  Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { derivePaymentStatus, formatMoney, paymentChannel, paymentEntityLink } from '@/client/types/payment';
import { usePaymentDetailQuery } from '@/client/api/payment/payment.query';
import {
  useCancelPaymentMutation,
  useFulfillPaymentMutation,
  useRefundPaymentMutation,
  useVerifyPaymentMutation,
} from '@/client/api/payment/payment.mutation';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  EntityRelationshipLink,
  OrgRelationshipLink,
  RelationshipLink,
  UserRelationshipLink,
} from '@/client/components/admin/Payment/RelationshipLink';

interface PaymentDetailsDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  pid: number | null;
  canEdit: boolean;
  canRefund: boolean;
  canVoid: boolean;
  onClose: () => void;
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  canceled: 'bg-gray-100 text-gray-700',
  refunded: 'bg-red-100 text-red-800',
};

const Flag: React.FC<{ label: string; on: boolean }> = ({ label, on }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
      on ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
    }`}
  >
    {label}: {on ? 'yes' : 'no'}
  </span>
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return raw;
  }
};

const isTruthy = (v: unknown) => v === 1 || v === true;

const PaymentDetailsDrawer: React.FC<PaymentDetailsDrawerProps> = ({
  isOpen,
  setIsOpen,
  pid,
  canEdit,
  canRefund,
  canVoid,
  onClose,
}) => {
  const [showRefund, setShowRefund] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');

  const { data: payment, isLoading } = usePaymentDetailQuery(isOpen ? pid : null);
  const refundMutation = useRefundPaymentMutation();
  const verifyMutation = useVerifyPaymentMutation();
  const fulfillMutation = useFulfillPaymentMutation();
  const cancelMutation = useCancelPaymentMutation();

  useEffect(() => {
    if (!isOpen) {
      setShowRefund(false);
      setRefundReason('');
      setRefundAmount('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const status = payment ? derivePaymentStatus(payment) : 'pending';
  const busy =
    refundMutation.isPending ||
    verifyMutation.isPending ||
    fulfillMutation.isPending ||
    cancelMutation.isPending;

  const handleRefund = async () => {
    if (!payment) return;
    if (!showRefund) {
      setShowRefund(true);
      return;
    }
    if (!refundReason.trim()) {
      toast.error('Refund reason is required', { richColors: true });
      return;
    }
    const idempotencyKey = generateIdempotencyKey();
    const body: { reason: string; amount?: number } = { reason: refundReason.trim() };
    if (refundAmount.trim()) {
      const amt = Number(refundAmount);
      if (!Number.isFinite(amt) || amt <= 0) {
        toast.error('Enter a valid refund amount', { richColors: true });
        return;
      }
      body.amount = amt;
    }
    try {
      const res = await refundMutation.mutateAsync({
        pid: payment.pid,
        body,
        idempotencyKey,
      });
      if (res?.warning === 'gateway_refunded_record_failed') {
        toast.error(
          'Gateway refund succeeded but the payment record failed to update. Reconcile manually.',
          { richColors: true, duration: 12_000 },
        );
      } else {
        toast.success('Payment refunded', { richColors: true });
      }
      setShowRefund(false);
      setRefundReason('');
      setRefundAmount('');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to refund payment', { richColors: true });
    }
  };

  const handleVerify = async () => {
    if (!payment) return;
    try {
      await verifyMutation.mutateAsync({ pid: payment.pid });
      toast.success('Payment verified', { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to verify payment', { richColors: true });
    }
  };

  const handleFulfill = async () => {
    if (!payment) return;
    try {
      await fulfillMutation.mutateAsync(payment.pid);
      toast.success('Payment fulfilled', { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fulfill payment', { richColors: true });
    }
  };

  const handleCancel = async () => {
    if (!payment) return;
    try {
      await cancelMutation.mutateAsync(payment.pid);
      toast.success('Payment canceled', { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to cancel payment', { richColors: true });
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="mx-auto flex max-h-[92vh] max-w-2xl flex-col">
        <DrawerHeader className="border-b border-gray-100 text-left">
          <DrawerTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            {isLoading ? 'Loading…' : `Payment #${payment?.pid ?? pid}`}
          </DrawerTitle>
          <DrawerDescription>
            {payment?.ffid
              ? `${payment.ffid}${payment.ref_id ? ` · Ref ${payment.ref_id}` : ''}`
              : payment?.ref_id
                ? `Ref ${payment.ref_id}`
                : 'Payment details'}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {!isLoading && payment && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[status]}`}
                >
                  {status.toUpperCase()}
                </span>
                <Flag label="verified" on={isTruthy(payment.verified)} />
                <Flag label="fulfilled" on={isTruthy(payment.fulfilled)} />
                <Flag label="canceled" on={isTruthy(payment.canceled)} />
                <Flag label="refund" on={isTruthy(payment.refund)} />
                {isTruthy(payment.tester) && <Flag label="tester" on />}
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-500">Amount</p>
                <p className="text-2xl font-black text-gray-900">
                  {formatMoney(payment.amount, payment.ccode)}
                </p>
                {payment.amount_paid != null && (
                  <p className="mt-1 text-sm text-gray-500">
                    Paid: {formatMoney(payment.amount_paid, payment.ccode)}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="PID" value={payment.pid} />
                <InfoRow label="FFID" value={payment.ffid || '—'} />
                <InfoRow label="UUID" value={payment.uuid || '—'} />
                <InfoRow label="Ref ID" value={payment.ref_id || '—'} />
                <InfoRow label="Channel" value={paymentChannel(payment)} />
                <InfoRow label="Source (psid)" value={payment.psid ?? '—'} />
                <InfoRow
                  label="Invoice"
                  value={
                    <RelationshipLink
                      kind="invoice"
                      id={payment.invid}
                      label={payment.invoice_ffid || (payment.invid != null ? `#${payment.invid}` : undefined)}
                      subtitle={payment.invoice_status ? String(payment.invoice_status) : undefined}
                    />
                  }
                />
                <InfoRow label="User" value={<UserRelationshipLink uid={payment.uid} summary={payment.user_summary} />} />
                <InfoRow label="Org" value={<OrgRelationshipLink oid={payment.oid} summary={payment.org_summary} />} />
                <InfoRow label="Voucher (vid)" value={payment.vid ?? '—'} />
                <InfoRow
                  label="Card"
                  value={
                    payment.bpmid != null ? (
                      <RelationshipLink
                        kind="card"
                        id={payment.bpmid}
                        label={`bpmid ${payment.bpmid}`}
                        extra={{
                          uid: payment.uid ?? undefined,
                          oid: payment.oid ?? undefined,
                          bpmid: payment.bpmid,
                        }}
                      />
                    ) : (
                      '—'
                    )
                  }
                />
                {(payment as { wid?: number | null }).wid != null && (
                  <InfoRow
                    label="Wallet"
                    value={
                      <RelationshipLink
                        kind="wallet"
                        id={(payment as { wid?: number }).wid}
                        label={`wid ${(payment as { wid?: number }).wid}`}
                        extra={{
                          uid: payment.uid ?? undefined,
                          oid: payment.oid ?? undefined,
                          wid: (payment as { wid?: number }).wid,
                        }}
                      />
                    }
                  />
                )}
                <InfoRow label="Added" value={formatDate(payment.date_added)} />
                <InfoRow label="Modified" value={formatDate(payment.date_modified)} />
              </div>

              {(() => {
                const entityLink = paymentEntityLink(payment);
                const entitySummary =
                  payment.entity_summary ||
                  (entityLink
                    ? {
                        eid: entityLink.eid,
                        name: entityLink.name,
                        slug: entityLink.slug,
                        ffid: entityLink.ffid,
                        uuid: entityLink.uuid,
                      }
                    : null);
                if (!entitySummary) return null;
                return (
                  <div className="rounded-lg border border-gray-100 p-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Linked entity
                    </p>
                    <EntityRelationshipLink eid={entitySummary.eid} summary={entitySummary} />
                  </div>
                );
              })()}

              {payment.ref_notes && <InfoRow label="Notes" value={payment.ref_notes} />}

              {showRefund && canRefund && (
                <div className="space-y-3 rounded-lg border border-red-100 bg-red-50 p-4">
                  <p className="text-sm font-semibold text-red-800">Refund payment</p>
                  <div className="space-y-1.5">
                    <Label>Reason</Label>
                    <Input
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="Why is this being refunded?"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Amount (optional — full if blank)</Label>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      placeholder={String(payment.amount ?? '')}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {canRefund && payment && status !== 'refunded' && (
              <Button
                type="button"
                variant={showRefund ? 'destructive' : 'outline'}
                size="sm"
                disabled={busy}
                onClick={handleRefund}
              >
                {refundMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1.5 h-4 w-4" />
                )}
                {showRefund ? 'Confirm refund' : 'Refund'}
              </Button>
            )}
            {canEdit && payment && !isTruthy(payment.verified) && (
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={handleVerify}>
                {verifyMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-1.5 h-4 w-4" />
                )}
                Verify
              </Button>
            )}
            {canEdit && payment && !isTruthy(payment.fulfilled) && status !== 'canceled' && (
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={handleFulfill}>
                {fulfillMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <PackageCheck className="mr-1.5 h-4 w-4" />
                )}
                Fulfill
              </Button>
            )}
            {canVoid && payment && status !== 'canceled' && status !== 'refunded' && (
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={handleCancel}>
                {cancelMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Ban className="mr-1.5 h-4 w-4" />
                )}
                Cancel
              </Button>
            )}
            {showRefund && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowRefund(false);
                  setRefundReason('');
                  setRefundAmount('');
                }}
              >
                Back
              </Button>
            )}
            <DrawerClose asChild>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default PaymentDetailsDrawer;
